import { FilterCondition } from "@irohana-dev/components";
import { FC, useCallback, useMemo } from "react";
import { TSavedConditionShareScope } from "@/api/services/main/saved-condition/dtos/saved-condition.dto";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useFilterConditionPanel } from "@/components/commons/filter-condition/use-filter-condition-panel";
import { TFilterConditionPanelConfig } from "@/components/commons/filter-condition/types";
import { CaseEditMatchFilterFormFields } from "@/features/case/components/CaseEdit/CaseEditMatchFilterFormFields";
import { fromFilterValue, toFilterValue } from "@/features/case/components/CaseEdit/case-edit-match-filter-condition-adapter";
import {
  LOCKED_CASE_EDIT_MATCH_COLUMN_IDS,
  TCaseEditMatchDisplayColumnSetting,
  TCaseEditMatchFilterFormExtended,
  defaultCaseEditMatchDisplayColumns,
  defaultCaseEditMatchFilterValues,
  normalizeCaseEditMatchMultiSelectFields,
  stripEmptyCaseEditMatchFilterFields,
} from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { parsePeriodOfStay } from "@/features/case/components/CaseMatches/case-match-filter-condition-adapter";
import { TOption } from "@/types/common";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { FILTER_CONDITION_PANEL_WIDTH } from "@/components/commons/filter-condition/filter-condition-panel-width";

type Props = {
  isOpen: boolean;
  mode?: "create" | "edit";
  pageType: string;
  columnsStorageKey: string;
  onClose: () => void;
  onApplyFilters?: (filters: TCaseEditMatchFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TCaseEditMatchDisplayColumnSetting[]) => void;
  initialFilters?: Partial<TCaseEditMatchFilterFormExtended>;
  initialDisplayColumns?: TCaseEditMatchDisplayColumnSetting[];
  appliedConditionId?: number | null;
  appliedConditionName?: string;
  appliedConditionShareScope?: TSavedConditionShareScope;
  appliedConditionIsDefault?: boolean;
  appliedConditionIncludeColumnSettings?: boolean;
  appliedConditionColumnSettings?: { order?: string[]; hidden?: string[] } | null;
  onConditionSaved?: (id: number, title: string) => void;
  interviewDateOptions?: TOption[];
  talentNumberOptions?: TOption[];
  fullNameOptions?: TOption[];
  nickNameOptions?: TOption[];
};

export const CaseEditMatchFilterConditionPanel: FC<Props> = ({
  mode = "create",
  pageType,
  columnsStorageKey,
  interviewDateOptions,
  talentNumberOptions,
  fullNameOptions,
  nickNameOptions,
  ...rest
}) => {
  const panelConfig = useMemo<TFilterConditionPanelConfig<TCaseEditMatchFilterFormExtended, TCaseEditMatchDisplayColumnSetting>>(
    () => ({
      pageType,
      defaultFilters: defaultCaseEditMatchFilterValues,
      defaultColumns: defaultCaseEditMatchDisplayColumns,
      columnsStorageKey,
      lockedColumnIds: LOCKED_CASE_EDIT_MATCH_COLUMN_IDS,
      normalizeFilters: (filters) => normalizeCaseEditMatchMultiSelectFields(filters),
      toFilterValue,
      fromFilterValue,
      stripEmptyFilters: stripEmptyCaseEditMatchFilterFields,
      getDateRange: (filters) => ({
        from: parsePeriodOfStay(filters.periodOfStayFrom),
        to: parsePeriodOfStay(filters.periodOfStayTo),
      }),
    }),
    [pageType, columnsStorageKey],
  );

  const panel = useFilterConditionPanel(panelConfig, { ...rest, mode });

  const handleFilterChange = useCallback(
    (field: keyof TCaseEditMatchFilterFormExtended, value: unknown) => {
      panel.setValue((prev) => toFilterValue({ ...fromFilterValue(prev), [field]: value }));
    },
    [panel.setValue],
  );

  return (
    <>
      <FilterCondition
        width={FILTER_CONDITION_PANEL_WIDTH}
        shareColor={filterConditionShareColor}
        isOpen={rest.isOpen}
        mode={panel.displayMode}
        onClose={rest.onClose}
        columns={panel.columnOptions}
        lockedColumns={LOCKED_CASE_EDIT_MATCH_COLUMN_IDS}
        columnOrder={panel.columnOrder}
        hiddenColumns={panel.hiddenColumns}
        onColumnSettingsChange={panel.handleColumnSettingsChange}
        conditionName={panel.conditionName}
        onConditionNameChange={panel.handleConditionNameChange}
        conditionNameError={panel.conditionNameError}
        appliedConditionId={rest.appliedConditionId ?? undefined}
        shareScope={panel.shareScope}
        onShareScopeChange={panel.setShareScope}
        isDefault={panel.isDefault}
        onIsDefaultChange={panel.setIsDefault}
        includeColumnSettings={panel.includeColumnSettings}
        onIncludeColumnSettingsChange={panel.setIncludeColumnSettings}
        hasSavedColumnSettings={panel.hasSavedColumnSettings}
        onSaveCondition={panel.handleSaveCondition}
        isSaving={panel.isSaving}
        onApply={panel.handleApply}
        onClear={() => panel.hasDataToClear && panel.setIsClearConfirmOpen(true)}
      >
        <CaseEditMatchFilterFormFields
          filterValues={panel.currentFilters}
          onFilterChange={handleFilterChange}
          interviewDateOptions={interviewDateOptions}
          talentNumberOptions={talentNumberOptions}
          fullNameOptions={fullNameOptions}
          nickNameOptions={nickNameOptions}
        />
      </FilterCondition>
      <ConfirmDeleteModal
        open={panel.isClearConfirmOpen}
        onClose={() => panel.setIsClearConfirmOpen(false)}
        onOk={panel.clearValue}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
